'use client'

import React, { createContext, useCallback, useContext, useState } from 'react'

// ============================================================================
// Types
// ============================================================================

export interface BreadcrumbItem {
  /** Display name */
  name: string
  /** Optional href for linking */
  href?: string
}

interface BreadcrumbContextValue {
  /** Current breadcrumb items set by the page */
  items: BreadcrumbItem[]
  /** Set breadcrumb items (call from page components) */
  setItems: (items: BreadcrumbItem[]) => void
  /** Clear breadcrumb items */
  clearItems: () => void
}

// ============================================================================
// Context
// ============================================================================

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null)

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access and modify breadcrumb items
 *
 * @example Setting breadcrumbs from a page
 * ```tsx
 * const { setItems, clearItems } = useBreadcrumb()
 *
 * useEffect(() => {
 *   setItems([
 *     { name: 'SBIR/STTR', href: '/sbirs/search' },
 *     { name: 'DOD' },
 *     { name: 'My Solicitation Title' },
 *   ])
 *   return () => clearItems()
 * }, [setItems, clearItems])
 * ```
 */
export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext)
  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider')
  }
  return context
}

/**
 * Hook that returns items only (for navbar consumption)
 * Returns empty array if no provider exists (graceful fallback)
 */
export function useBreadcrumbItems(): BreadcrumbItem[] {
  const context = useContext(BreadcrumbContext)
  return context?.items ?? []
}

// ============================================================================
// Provider
// ============================================================================

interface BreadcrumbProviderProps {
  children: React.ReactNode
}

/**
 * Provider for dynamic breadcrumb data
 *
 * Wrap your layout with this provider to allow pages to set breadcrumb items.
 */
export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({
  children,
}) => {
  const [items, setItemsState] = useState<BreadcrumbItem[]>([])

  const setItems = useCallback((newItems: BreadcrumbItem[]) => {
    setItemsState(newItems)
  }, [])

  const clearItems = useCallback(() => {
    setItemsState([])
  }, [])

  return (
    <BreadcrumbContext.Provider value={{ items, setItems, clearItems }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}
